/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import clsx from 'clsx'
import {UiTableActionsCell, UiTableCell, UiTableRow,} from 'components/Ui/UiTable'
import {LeadTask} from 'types/entities/LeadTask'
import {LeadTaskStatus} from 'types/enums/LeadTaskStatus'
import Dates from 'utils/Dates'
import UiIconButton from 'components/Ui/UiIconButton'
import {CheckIcon, PencilIcon, TrashIcon} from '@heroicons/react/24/solid'
import {useTranslation} from "react-i18next";

interface EditLeadModalTasksListProps {
    tasks: LeadTask[]
    onEdit: (task: LeadTask) => void
    onDelete: (task: LeadTask) => void
    onComplete: (task: LeadTask) => void
}

const EditLeadModalTasksList = (props: EditLeadModalTasksListProps) => {
	const {t} = useTranslation()
    if (!props.tasks.length) {
        return null
    }

    return (
        <>
            {props.tasks.map((task) => {
                const isCompleted = !!task.completed_at || task.status === LeadTaskStatus.Done
                return (
                <UiTableRow key={task.id} className="EditLeadModalTasksList">
                    <UiTableCell>{t(task.type)}</UiTableCell>
                    <UiTableCell>{t(task.status)}</UiTableCell>
                    <UiTableCell>{t(task.title)}</UiTableCell>
                    <UiTableCell>
                        <span>{Dates.dateTime(task.due_at)}</span>
                    </UiTableCell>
                    <UiTableCell>
                        <div
                            className={clsx(
                                'EditLeadModalDescriptionCell',
                                'max-w-xs truncate'
                            )}
                        >
                            {task.description || '--'}
                        </div>
                    </UiTableCell>
                    <UiTableCell>
                        {isCompleted && task.completed_at ? (
                            <span className="text-green-600">{Dates.dateTime(task.completed_at)}</span>
                        ) : (
                            <span className="EditLeadModalPlaceholderText text-gray-400">
                                -
                            </span>
                        )}
                    </UiTableCell>
                    <UiTableActionsCell>
                        {!isCompleted && (
                            <UiIconButton
                                onClick={() => props.onComplete(task)}
                                className={clsx('text-green-600 hover:text-green-900')}
                                title={t('Complete')}
                                variant={'light'}
                                size={'sm'}
                            >
                                <CheckIcon className={clsx('w-5 h-5')} />
                            </UiIconButton>
                        )}
                        <UiIconButton
                            onClick={() => props.onEdit(task)}
                            className={clsx(
                                'text-blue-600 hover:text-blue-900'
                            )}
                            title={t('Edit')}
                            variant={'light'}
                            size={'sm'}
                        >
                            <PencilIcon className={clsx('w-5 h-5')} />
                        </UiIconButton>
                        <UiIconButton
                            onClick={() => props.onDelete(task)}
                            className={clsx('text-red-600 hover:text-red-900')}
                            title={t('Delete')}
                            variant={'light'}
                            size={'sm'}
                        >
                            <TrashIcon className={clsx('w-5 h-5')} />
                        </UiIconButton>
                    </UiTableActionsCell>
                </UiTableRow>
                )
            })}
        </>
    )
}

export default EditLeadModalTasksList
